import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { callMetricsService } from "../../services/callMetricsService";
import {
  Metric,
  LatencySummary,
  TimelineEvent,
  MetricsDashboardState,
} from "../../types/metrics";
import { LatencyOverview } from "./LatencyOverview";
import { LatencyTimeline } from "./LatencyTimeline";
import { LatencyStatistics } from "./LatencyStatistics";
import { ResponseLatencyBreakdown } from "./ResponseLatencyBreakdown";

export const CallLatencyDashboard: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();

  const [state, setState] = useState<MetricsDashboardState>({
    callId: callId || "",
    metrics: [],
    summary: null,
    timeline: [],
    loading: true,
    error: null,
    filter: {},
  });

  const [refreshing, setRefreshing] = useState(false);

  /**
   * Fetch all metrics data for the call
   */
  const fetchMetricsData = async () => {
    if (!callId) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Call ID is required",
      }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Fetch metrics, summary, and timeline in parallel
      const [metricsResponse, summaryResponse, timelineResponse] =
        await Promise.all([
          callMetricsService.getMetrics(callId, state.filter),
          callMetricsService.getLatencySummary(callId),
          callMetricsService.getResponseTimeline(callId),
        ]);

      setState((prev) => ({
        ...prev,
        metrics: metricsResponse.metrics,
        summary: summaryResponse as LatencySummary,
        timeline: timelineResponse.timeline,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to load metrics",
      }));
    }
  };

  /**
   * Handle refresh button click
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMetricsData();
    setRefreshing(false);
  };

  // Fetch data on mount and when callId changes
  useEffect(() => {
    fetchMetricsData();
  }, [callId]);

  // Loading state
  if (state.loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading metrics data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{state.error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Retry"
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // No data state
  if (!state.metrics || state.metrics.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-8">
          <div className="text-center">
            <p className="text-lg text-muted-foreground mb-4">
              No metrics data available for this call.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Metrics may not have been collected yet, or the call may still be in
              progress.
            </p>
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
              {refreshing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Call Latency Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Performance metrics and latency analysis for call {callId?.slice(0, 8)}...
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Summary Cards */}
      {state.summary && <LatencyOverview summary={state.summary} />}

      {/* Timeline Chart */}
      <LatencyTimeline metrics={state.metrics} />

      {/* Response Breakdown */}
      {state.timeline && state.timeline.length > 0 && (
        <ResponseLatencyBreakdown timeline={state.timeline} />
      )}

      {/* Statistics Table */}
      {state.summary && <LatencyStatistics summary={state.summary} />}
    </div>
  );
};
